import { observable, action } from 'mobx';
import { Helper } from '../../../Helper';
import { GroupListItem, GroupList, GroupListColumnType, GroupListGroup, GroupListTitleColumn } from '../../../interfaces/IGroupList';
import { ITargetingGroup } from '../../../interfaces/ITargetingGroup';
import { ITargetingGroupValues } from '../../../interfaces/ITargetingGroupValues';
import { ITargetingProperties } from '../../../interfaces/ITargetingProperties';
import { ITargetTerm } from '../../../interfaces/ITargetTerm';
import { IApplication } from '../../../interfaces/IApplication';
import * as React from 'react';
import { IAIcon } from '../../icon/Icon';
import { LocalizationService } from '../../../services/LocalizationService';
import { ITenantSettings } from '../../../interfaces/ITenantSettings';

export class TargetingAdminStore {

  // Props
  @observable public webpartId?: string;
  @observable public language: "Danish" | "English" | "Swedish" | "German" | "Portuguese";
  @observable public id: string = Helper.getRandomStringKey();
  @observable public width: number;
  @observable public loading: boolean;
  @observable public token: string;
  @observable public tenant: string;
  @observable public selectedTab: string;
  // @observable public apiUrl: string;
  @observable public environment: "Development" | "Test" | "Production";
  @observable public groupListItem: GroupListItem[];
  @observable public groupedList: GroupList;
  @observable public greaterTitleWidth: number = 0;
  @observable public groups: GroupListGroup[];
  @observable public groupsList: ITargetingGroup[];
  @observable public highlightColor: string = "#23879b"; // #1c899d //23879b

  @observable public selectedUserGroup: ITargetingGroup;
  @observable public userGroupToEdit: ITargetingGroup;
  @observable public userGroupToDelete: ITargetingGroup;
  @observable public originalUserGroupToEdit: ITargetingGroup;
  @observable public userGroupIndex: number;
  @observable public showCancelEditPopup: boolean;

  @observable public creatingNew: boolean;
  @observable public hasChanged: string;
  @observable public reloadComponent: boolean;

  @observable public targetingProperties: ITargetingProperties[];
  @observable public termsList: ITargetTerm[];
  @observable public userPropertiesList: string[];

  @observable public instancesList: { [key: string]: Array<{ id?: number, title: string, instance: string }> };
  @observable public applicationsData: IApplication[];

  @observable public isLoadingProfileData: boolean;
  @observable public isLoadingApplication: boolean;
  @observable public showCancelPropertyPopUp: boolean;
  @observable public showAddButton: boolean;

  @observable public settings: ITenantSettings;

  @action public updateUserProperties(list: string[]) {
    this.userPropertiesList = list;
  }

  @action public updateTerms(list: ITargetTerm[]) {
    this.termsList = list;
  }

  @action public updatePropertiesList(list: ITargetingProperties[]) {
    this.targetingProperties = list;
  }

  @action public reload(): void {
    this.reloadComponent = true;
    this.hasChanged = Helper.getRandomStringKey();
  }

  // Language

  @action public setLanguage(language: "Danish" | "English" | "Swedish" | "German" | "Portuguese") {
    this.language = language;
    Helper.setLocalStorage("IA_language", language);
  }

  @action public getGroups(): void {
    const columnsTitle: GroupListTitleColumn[] = [
      {
        title: new LocalizationService().strings.TargetingAdmin_UserGroup_GroupLabel,
        name: "Title",
        width: this.greaterTitleWidth && this.greaterTitleWidth > 0 ? this.greaterTitleWidth.toString() + "px" : null,
        onClick: () => this.groupedList.groups.map((group) => {
          this.sortGroupsByDisplayNameOrUsersAndGroups(group, new LocalizationService().strings.TargetingAdmin_UserGroup_GroupLabel)
        })
      },
      {
        title: "",
        name: "Edit"
      },
      {
        title: new LocalizationService().strings.TargetingAdmin_UserGroup_GroupValues,
        name: "Values",
        onClick: () => this.groupedList.groups.map((group) => {
          this.sortGroupsByDisplayNameOrUsersAndGroups(group, new LocalizationService().strings.TargetingAdmin_UserGroup_GroupValues)
        })
      }];
    const items = this.groupListItem;
    this.groups = [{
      name: "group1",
      expanded: true,
      title: "",
      items,
      hideTitle: true,
      borderEveryItem: true
    }];

    this.groupedList = {
      columns: columnsTitle,
      groups: this.groups,
      hideFirstColumn: true
    };
  }

  // Saving

  @observable public isSaving: boolean;


  @action private sortGroupsByDisplayNameOrUsersAndGroups = (groups: GroupListGroup, columnTitle: string) => {
    if (columnTitle == new LocalizationService().strings.TargetingAdmin_UserGroup_GroupValues) {
      groups.items = groups.items.sort((v1, v2) => {
        let v = 0;
        v1.columns.map((c1) => {
          v2.columns.map((c2) => {
            if (c1.field == "Values" && c2.field == "Values") {
              if (c1.value.toLocaleLowerCase() > c2.value.toLocaleLowerCase()) {
                v = 1;
              }
              if (c1.value.toLocaleLowerCase() < c2.value.toLocaleLowerCase()) {
                v = -1;
              }
            }
          })
        })
        return v;
      });
    }
    if (columnTitle == new LocalizationService().strings.TargetingAdmin_UserGroup_GroupLabel) {
      groups.items = groups.items.sort((v1, v2) => {
        if (v1.title.toLocaleLowerCase() > v2.title.toLocaleLowerCase()) {
          return 1;
        }
        if (v1.title.toLocaleLowerCase() < v2.title.toLocaleLowerCase()) {
          return -1;
        }
        return 0;
      })
    }
    return groups;
  }


  @action public transformDataToGroupList = (groups: ITargetingGroup[]) => {

    this.groupsList = groups.sort((g1, g2) => {
      if (g1.title.toLocaleLowerCase() > g2.title.toLocaleLowerCase()) {
        return 1;
      }
      if (g1.title.toLocaleLowerCase() < g2.title.toLocaleLowerCase()) {
        return -1;
      }
      return 0;
    });
    const items: GroupListItem[] = [];
    this.greaterTitleWidth = 0;

    groups.forEach(x => {

      const item = {
        title: x.title,
        id: x.id,
        name: x.title,
        columns: []
      };

      const itemWidthInPixel = 8 * (x.title.length) + 18;

      if (itemWidthInPixel > this.greaterTitleWidth) {
        this.greaterTitleWidth = itemWidthInPixel;
      }

      item.columns.push({
        field: "Title",
        title: new LocalizationService().strings.TargetingAdmin_UserGroup_GroupLabel,
        type: GroupListColumnType.Text,
        value: (
          <div style={{ position: 'relative', top: '3px' }}>
            <div style={{ float: 'left', position: 'relative', top: '5px' }}>
              <IAIcon
                title={"People"}
                color={this.highlightColor}
                size={17} />
            </div>
            <div style={{ float: 'left', position: 'relative', marginLeft: 7, marginTop: 3 }}>{x.title}</div>
          </div>
        ),
        width: itemWidthInPixel
      });

      item.columns.push({
        field: "Edit",
        title: "",
        type: GroupListColumnType.Icon,
        value: true
      });

      let values: ITargetingGroupValues[];
      let valueNames = "";

      if (x.values) {
        values = (JSON.parse(x.values) as ITargetingGroupValues[]).sort((v1, v2) => {
          if (v1.displayName.toLocaleLowerCase() > v2.displayName.toLocaleLowerCase()) {
            return 1;
          }
          if (v1.displayName.toLocaleLowerCase() < v2.displayName.toLocaleLowerCase()) {
            return -1;
          }
          return 0;
        });

        values.forEach((_) => {
          valueNames += _.displayName + "\n";
        });
      }

      item.columns.push({
        field: "Values",
        title: new LocalizationService().strings.TargetingAdmin_UserGroup_GroupValues,
        type: GroupListColumnType.Text,
        value: valueNames
      });

      items.push(item);

    });

    this.groupListItem = items;
    this.getGroups();

  }
}